/**
 * services/CommunicationService.ts
 * Communication tracking service for email audit trail
 *
 * Handles tracking of all email communications sent from the system
 * with full audit trail capabilities. Generic design allows tracking
 * communications to any entity type (beta applications, users, etc.).
 *
 * Architecture: WU1.5 Task 9 - Repository pattern
 * All database access delegated to communicationsRepository
 */

import * as communicationsRepository from '@/repositories/communicationsRepository';
import type {
  Communication,
  CommunicationEntityType,
  CommunicationStatus
} from '@/types/supabase';
import type { EmailTemplate, EmailTemplateVariables } from '@/types/email';
import type {
  CommunicationWithTemplate,
  CommunicationStats,
  InvitationStats
} from '@/repositories/communicationsRepository';

/**
 * Communication Service
 *
 * Business logic layer for communication tracking.
 * All database access delegated to communicationsRepository.
 */
export class CommunicationService {
  constructor() {
    // TODO: [CONTRACTOR] Integrate with real email service for delivery webhook processing and status updates
  }

  // ===== COMMUNICATION CREATION METHODS =====

  /**
   * Record a new email communication
   *
   * @param entityType - Type of entity receiving the communication
   * @param entityId - ID of the entity
   * @param recipientEmail - Email address of recipient
   * @param template - Email template used
   * @param variables - Variables used in template rendering
   * @param sentBy - Admin who sent the email
   * @param messageId - Optional message ID from email service
   * @returns Promise resolving to communication record
   */
  async recordEmailSent(
    entityType: CommunicationEntityType,
    entityId: string,
    recipientEmail: string,
    template: EmailTemplate,
    variables: EmailTemplateVariables,
    sentBy: string,
    messageId?: string
  ): Promise<Communication> {
    try {
      // TODO: [CONTRACTOR] Implement rate limiting integration - check email service sending limits before recording communications

      // Render template content for snapshot
      const renderedSubject = this.renderTemplateContent(template.subject, variables);
      const renderedContent = this.renderTemplateContent(template.content, variables);

      const communicationData = {
        entityType,
        entityId,
        recipientEmail,
        templateId: template.id,
        templateSnapshot: {
          subject: renderedSubject,
          content: renderedContent,
          template_type: template.templateType,
          variables_used: variables as unknown as Record<string, string>
        },
        sentBy,
        status: 'sent' as CommunicationStatus,
        messageId: messageId || null,
        providerMessageId: messageId || null, // Capture provider message ID at insert
        sentAt: new Date().toISOString(),
        deliveredAt: null,
        errorMessage: null
      };

      const communication = await communicationsRepository.create(communicationData);

      // TODO: [CONTRACTOR] Implement analytics tracking hook for communication events (sent emails, delivery rates, engagement metrics)

      return communication;
    } catch (error) {
      console.error('Failed to record email communication:', error);
      throw new Error('Failed to record email communication');
    }
  }

  /**
   * Record a failed email communication
   *
   * @param entityType - Type of entity
   * @param entityId - ID of the entity
   * @param recipientEmail - Email address
   * @param template - Email template
   * @param variables - Template variables
   * @param sentBy - Admin who attempted to send
   * @param errorMessage - Error message from email service
   * @returns Promise resolving to communication record
   */
  async recordEmailFailed(
    entityType: CommunicationEntityType,
    entityId: string,
    recipientEmail: string,
    template: EmailTemplate,
    variables: EmailTemplateVariables,
    sentBy: string,
    errorMessage: string
  ): Promise<Communication> {
    try {
      const communicationData = {
        entityType,
        entityId,
        recipientEmail,
        templateId: template.id,
        templateSnapshot: {
          subject: template.subject,
          content: template.content,
          template_type: template.templateType,
          variables_used: variables as unknown as Record<string, string>
        },
        sentBy,
        status: 'failed' as CommunicationStatus,
        messageId: null,
        sentAt: new Date().toISOString(),
        deliveredAt: null,
        errorMessage
      };

      return await communicationsRepository.create(communicationData);
    } catch (error) {
      console.error('Failed to record email failure:', error);
      throw new Error('Failed to record email failure');
    }
  }

  // ===== COMMUNICATION QUERY METHODS =====

  /**
   * Get all communications for a specific entity
   *
   * @param entityType - Type of entity
   * @param entityId - ID of the entity
   * @returns Promise resolving to array of communications
   */
  async getByEntity(
    entityType: CommunicationEntityType,
    entityId: string
  ): Promise<Communication[]> {
    try {
      return await communicationsRepository.getByEntity(entityType, entityId);
    } catch (error) {
      console.error(`Failed to get communications for ${entityType}:${entityId}:`, error);
      throw new Error('Failed to retrieve communications');
    }
  }

  /**
   * Get communications with template information
   *
   * @param entityType - Type of entity
   * @param entityId - ID of the entity
   * @returns Promise resolving to communications with template data
   */
  async getByEntityWithTemplates(
    entityType: CommunicationEntityType,
    entityId: string
  ): Promise<CommunicationWithTemplate[]> {
    try {
      return await communicationsRepository.getByEntityWithTemplates(entityType, entityId);
    } catch (error) {
      console.error(`Failed to get communications with templates for ${entityType}:${entityId}:`, error);
      throw new Error('Failed to retrieve communications with templates');
    }
  }

  /**
   * Check if a specific email type has been sent to an entity
   *
   * @param entityType - Type of entity
   * @param entityId - ID of the entity
   * @param templateType - Email template type to check
   * @returns Promise resolving to boolean
   */
  async hasEmailBeenSent(
    entityType: CommunicationEntityType,
    entityId: string,
    templateType: string
  ): Promise<boolean> {
    try {
      return await communicationsRepository.hasEmailBeenSent({
        entityType,
        entityId,
        templateType
      });
    } catch (error) {
      console.error(`Failed to check if ${templateType} email sent to ${entityType}:${entityId}:`, error);
      return false;
    }
  }

  // ===== STATUS UPDATE METHODS =====

  /**
   * Update communication status (e.g., mark as delivered)
   *
   * @param id - Communication ID
   * @param status - New status
   * @param deliveredAt - Optional delivery timestamp
   * @returns Promise resolving to updated communication
   */
  async updateStatus(
    id: string,
    status: CommunicationStatus,
    deliveredAt?: string
  ): Promise<Communication> {
    try {
      // TODO: [CONTRACTOR] Enhance with email service webhook integration - automatically update delivery status from real email service callbacks

      const updateData = {
        status,
        ...(deliveredAt && { deliveredAt })
      };

      return await communicationsRepository.update(id, updateData);
    } catch (error) {
      console.error(`Failed to update communication status:`, error);
      throw new Error('Failed to update communication status');
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Render template content with variables
   *
   * @param template - Template string
   * @param variables - Variables to substitute
   * @returns Rendered template
   */
  private renderTemplateContent(template: string, variables: EmailTemplateVariables): string {
    let rendered = template;

    // Replace all template variables
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      rendered = rendered.replace(regex, value || '');
    });

    // Clean up any remaining empty variables
    rendered = rendered.replace(/\{\{[^}]+\}\}/g, '');

    // Clean up multiple spaces and newlines
    rendered = rendered.replace(/\n\s*\n\s*\n/g, '\n\n');
    rendered = rendered.replace(/  +/g, ' ');

    return rendered.trim();
  }

  /**
   * Get communication statistics for reporting
   *
   * @param entityType - Optional entity type filter
   * @returns Promise resolving to communication stats
   */
  async getStats(entityType?: CommunicationEntityType): Promise<CommunicationStats> {
    try {
      // TODO: [CONTRACTOR] Implement data retention and PII handling policies - GDPR compliance for communication records, automatic data purging

      return await communicationsRepository.getStats(entityType);
    } catch (error) {
      console.error('Failed to get communication stats:', error);
      throw new Error('Failed to retrieve communication statistics');
    }
  }

  // ===== INVITATION-SPECIFIC COMMUNICATION METHODS =====

  /**
   * Record an invitation email communication
   *
   * @param betaApplicationId - Beta application ID receiving the invitation
   * @param recipientEmail - Email address of recipient
   * @param template - Invitation email template used
   * @param variables - Variables used in invitation template rendering
   * @param sentBy - Admin who sent the invitation
   * @param messageId - Optional message ID from email service
   * @returns Promise resolving to communication record
   */
  async recordInvitationSent(
    betaApplicationId: string,
    recipientEmail: string,
    template: EmailTemplate,
    variables: Record<string, string>,
    sentBy: string,
    messageId?: string
  ): Promise<Communication> {
    try {
      return await this.recordEmailSent(
        'beta_application',
        betaApplicationId,
        recipientEmail,
        template,
        variables as unknown as EmailTemplateVariables,
        sentBy,
        messageId
      );
    } catch (error) {
      console.error('Failed to record invitation email:', error);
      throw new Error('Failed to record invitation email communication');
    }
  }

  /**
   * Record a password reset email communication
   *
   * @param userId - User ID receiving the password reset
   * @param recipientEmail - Email address of recipient
   * @param template - Password reset email template used
   * @param variables - Variables used in password reset template rendering
   * @param sentBy - Admin who sent the password reset
   * @param messageId - Optional message ID from email service
   * @returns Promise resolving to communication record
   */
  async recordPasswordResetSent(
    userId: string,
    recipientEmail: string,
    template: EmailTemplate,
    variables: Record<string, string>,
    sentBy: string,
    messageId?: string
  ): Promise<Communication> {
    try {
      return await this.recordEmailSent(
        'user',
        userId,
        recipientEmail,
        template,
        variables as unknown as EmailTemplateVariables,
        sentBy,
        messageId
      );
    } catch (error) {
      console.error('Failed to record password reset email:', error);
      throw new Error('Failed to record password reset email communication');
    }
  }

  /**
   * Record an account disabled email communication
   *
   * @param userId - User ID whose account was disabled
   * @param recipientEmail - Email address of recipient
   * @param template - Account disabled email template used
   * @param variables - Variables used in disabled template rendering
   * @param sentBy - Admin who disabled the account
   * @param messageId - Optional message ID from email service
   * @returns Promise resolving to communication record
   */
  async recordAccountDisabledSent(
    userId: string,
    recipientEmail: string,
    template: EmailTemplate,
    variables: Record<string, string>,
    sentBy: string,
    messageId?: string
  ): Promise<Communication> {
    try {
      return await this.recordEmailSent(
        'user',
        userId,
        recipientEmail,
        template,
        variables as unknown as EmailTemplateVariables,
        sentBy,
        messageId
      );
    } catch (error) {
      console.error('Failed to record account disabled email:', error);
      throw new Error('Failed to record account disabled email communication');
    }
  }

  /**
   * Record an account reactivated email communication
   *
   * @param userId - User ID whose account was reactivated
   * @param recipientEmail - Email address of recipient
   * @param template - Account reactivated email template used
   * @param variables - Variables used in reactivated template rendering
   * @param sentBy - Admin who reactivated the account
   * @param messageId - Optional message ID from email service
   * @returns Promise resolving to communication record
   */
  async recordAccountReactivatedSent(
    userId: string,
    recipientEmail: string,
    template: EmailTemplate,
    variables: Record<string, string>,
    sentBy: string,
    messageId?: string
  ): Promise<Communication> {
    try {
      return await this.recordEmailSent(
        'user',
        userId,
        recipientEmail,
        template,
        variables as unknown as EmailTemplateVariables,
        sentBy,
        messageId
      );
    } catch (error) {
      console.error('Failed to record account reactivated email:', error);
      throw new Error('Failed to record account reactivated email communication');
    }
  }

  /**
   * Check if an invitation email has been sent to a user
   *
   * @param userId - User ID to check
   * @param templateType - Invitation template type ('invitation' or 'activation-reminder')
   * @returns Promise resolving to boolean
   */
  async hasInvitationBeenSent(
    userId: string,
    templateType: 'invitation' | 'activation-reminder'
  ): Promise<boolean> {
    try {
      return await this.hasEmailBeenSent('user', userId, templateType);
    } catch (error) {
      console.error(`Failed to check if ${templateType} email sent to user ${userId}:`, error);
      return false;
    }
  }

  /**
   * Get all invitation-related communications for a user
   *
   * @param userId - User ID to get communications for
   * @returns Promise resolving to invitation communications with template data
   */
  async getInvitationCommunications(userId: string): Promise<CommunicationWithTemplate[]> {
    try {
      const communications = await this.getByEntityWithTemplates('user', userId);

      // Filter for invitation-related templates only
      return communications.filter(comm =>
        comm.communication.templateSnapshot?.template_type === 'invitation' ||
        comm.communication.templateSnapshot?.template_type === 'activation-reminder'
      );
    } catch (error) {
      console.error(`Failed to get invitation communications for user ${userId}:`, error);
      throw new Error('Failed to retrieve invitation communications');
    }
  }

  /**
   * Get invitation communication statistics
   *
   * @returns Promise resolving to invitation-specific communication stats
   */
  async getInvitationStats(): Promise<InvitationStats> {
    try {
      return await communicationsRepository.getInvitationStats();
    } catch (error) {
      console.error('Failed to get invitation communication stats:', error);
      throw new Error('Failed to retrieve invitation communication statistics');
    }
  }
}

// Export singleton instance
export const communicationService = new CommunicationService();